var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

import React, { forwardRef, Children, isValidElement, Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { safeElement, responsiveArraySpreader, breakPointArrayCleaner } from "../../../utils";
import { FiChevronDown } from "react-icons/fi";
import { formStyleMixins } from "../utils/formStyleMixins";
import { FieldMessage } from "../FieldMessage";
import { media } from "../../../mixins";

var validTones = {
  danger: "danger",
  warning: "warning",
  success: "success",
  neutral: "neutral"
};

var Dropdown = forwardRef(function (_ref, ref) {
  var children = _ref.children,
      placeholder = _ref.placeholder,
      message = _ref.message,
      reserveMessageSpace = _ref.reserveMessageSpace,
      _ref$tone = _ref.tone,
      tone = _ref$tone === undefined ? validTones.neutral : _ref$tone,
      required = _ref.required,
      value = _ref.value,
      _ref$labelSize = _ref.labelSize,
      labelSize = _ref$labelSize === undefined ? undefined : _ref$labelSize,
      restProps = _objectWithoutProperties(_ref, ["children", "placeholder", "message", "reserveMessageSpace", "tone", "required", "value", "labelSize"]);

  Children.forEach(children, function (child) {
    if (!(isValidElement(child) && /^(option|optgroup)$/.test(child.type))) {
      throw new Error("`Dropdown` only accepts children of type `option` or `optgroup`.");
    }
  });

  return React.createElement(
    Fragment,
    null,
    React.createElement(
      Wrapper,
      null,
      React.createElement(
        Select,
        _extends({
          $as: "select",
          value: value || "",
          reserveMessageSpace: reserveMessageSpace,
          forwardedRefSafe: ref,
          tone: tone
          // tabIndex="1"
          , "aria-required": required,
          "aria-invalid": tone === validTones.danger,
          labelSizeArray: labelSize ? breakPointArrayCleaner(labelSize) : undefined
        }, restProps),
        React.createElement(
          "option",
          { value: "", disabled: true },
          placeholder
        ),
        React.createElement(
          Fragment,
          null,
          children
        )
      ),
      React.createElement(FoldIcon, null)
    ),
    React.createElement(FieldMessage, {
      message: message,
      tone: tone,
      reserveMessageSpace: reserveMessageSpace,
      labelSize: labelSize
    })
  );
});

export { Dropdown };
Dropdown.displayName = "Dropdown";

Dropdown.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node.isRequired,
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
} : {};

var Wrapper = styled.div.withConfig({
  displayName: "Dropdown__Wrapper",
  componentId: "mddko7-0"
})(["position:relative;width:100%;"]);

var Select = styled(safeElement()).withConfig({
  displayName: "Dropdown__Select",
  componentId: "mddko7-1"
})(["box-sizing:border-box;display:flex;justify-content:center;align-items:center;width:100%;max-width:100%;height:", "rem;padding:0 0 0 1rem;margin:0;font-family:inherit;", " line-height:1.4rem;border-radius:0;appearance:none;-moz-appearance:none;-webkit-appearance:none;background-color:white;background:white;&::-ms-expand{display:none;}option{font-weight:normal;}&:focus{outline:none;color:", ";}color:", ";", ";"], function (props) {
  return props.theme.buttonDefaultHeight;
}, function (props) {
  return props.labelSizeArray ? responsiveArraySpreader(function (value) {
    return css(["font-size:", "rem;"], value);
  }, props.labelSizeArray) : css(["font-size:16px;", ""], media.up.desktop(css(["font-size:20px;"])));
}, function (props) {
  return props.theme.textMedium;
}, function (props) {
  return props.value ? props.theme.textMedium : props.theme.textSoft;
}, formStyleMixins.borderToneColors);

var FoldIcon = styled(FiChevronDown).withConfig({
  displayName: "Dropdown__FoldIcon",
  componentId: "mddko7-2"
})(["position:absolute;top:50%;right:0.75rem;padding:0;background-color:rgba(250,250,250,1);box-shadow:0 0 5px 5px rgba(250,250,250,1);transform:translateY(-50%);z-index:10000;font-size:1.5rem;pointer-events:none;"]);